import { Component } from "react";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { object } from "prop-types";
import { Fill, Stroke, Style } from "ol/style";
import { unByKey } from "ol/Observable";
import { Set, Map } from "immutable";
import _ from "lodash";
import { TopoJSON } from "ol/format";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fromEvent, Subject } from "rxjs";
import { Select } from "ol/interaction";
import { pointerMove } from "ol/events/condition";
import {
	bufferCount,
	distinctUntilChanged,
	filter,
	map as mapOp,
	mergeMap,
	tap
} from "rxjs/operators";
import {
	createSelectorCreator,
	createSelector,
	defaultMemoize
} from "reselect";
import {
	addResourceId as ari,
	incrementLoadedContainersCount as ilcc
} from "../data/ducks";
import { clearResource as cr, selectResource as sr } from "./ducks";
import { is } from "immutable";

import { loadContainer as lc } from "../filters/ducks";
import { getColorFeature } from "./functions";

const immutableSelector = createSelectorCreator(defaultMemoize, is);

const indicatorSelector = (state) => state.filters.get("disease");
const indicatorsMapSelector = (state) => state.filters.get("indicators");

const paletteSelector = createSelector(
	indicatorSelector,
	indicatorsMapSelector,
	(indicator, map) => _.get(map, `${indicator}.paletteType`)
);

const getDataFilter = ({ data }, props) => {
	const groupContainer = data.get("groups").get(props.data.id) || Set();
	const currentData = data.get("data");
	return groupContainer
		.map((item) => currentData.get(item))
		.filterNot(_.isNil)
		.toJS();
};

const dataSelector = immutableSelector(getDataFilter, (items) => {
	const result = items || [];
	return result.reduce(
		(map, data) =>
			map.set(data.resourceId, {
				value: +data.currentValue
			}),
		Map({})
	);
});

const selectedResourceSelector = (state) => state.map.get("selectedResource");
const resourceSelector = createSelector(selectedResourceSelector, _.identity);

const styleVector = new Style({
	fill: new Fill({
		color: "rgba(255, 255, 255, 0.6)"
	}),
	stroke: new Stroke({
		color: "#319FD3",
		width: 1
	})
});

function selectStyleDefault(feature) {
	return new Style({
		fill: new Fill({
			color: "rgba(255, 255, 255, 0.6)"
		}),
		stroke: new Stroke({
			color: "#319FD3",
			width: 1
		})
	});
}

function selectedStyle(feature) {
	const color = feature.get("color") || "rgba(255, 255, 255, 0.6)";
	return new Style({
		fill: new Fill({
			color
		}),
		stroke: new Stroke({
			color: "#d33131",
			width: 3
		})
	});
}

function deselectedStyle(feature) {
	const color = feature.get("color") || "rgba(255, 255, 255, 0.6)";
	return new Style({
		fill: new Fill({
			color
		}),
		stroke: new Stroke({
			color: "#319FD3",
			width: 1
		})
	});
}

const extractUUIDFromEvent = mapOp((e) => e.values_.uuid);

class MapLayer extends Component {
	static propTypes = {
		map: object
	};

	updateDataSubject = new Subject();
	updateStyleSelected = new Subject();
	features = {};
	layer = null;

	componentDidMount() {
		const {
			map,
			data: { id, url },
			addResourceId,
			loadContainer,
			selectResource,
			clearResource,
			incrementLoadedContainersCount
		} = this.props;
		const source = new VectorSource({
			url: url,
			format: new TopoJSON({}),
			overlaps: false
		});

		const listenerKey = source.on("change", function(e) {
			if (source.getState() === "ready") {
				loadContainer(id);
				incrementLoadedContainersCount();
				// remove event listener
				unByKey(listenerKey);
			}
		});

		const vector = new VectorLayer({
			source,
			style: styleVector
		});

		this.saveVector(source);
		map.addLayer(vector);
		this.layer = vector;

		const feature$ = fromEvent(source, "addfeature").pipe(
			tap((e) => (this.features[e.feature.values_.uuid] = e.feature)),
			tap((e) => addResourceId(e.feature.ol_uid, e.feature.values_.uuid, id)),
			mapOp((e) => e.feature),
			extractUUIDFromEvent,
			bufferCount(50)
		);
		feature$.subscribe(() => {});

		const select = new Select({
			condition: pointerMove,
			layers: [vector],
			style: selectStyleDefault
		});

		map.addInteraction(select);

		//const selectStream = fromEvent(select, "select").pipe(share());

		fromEvent(select, "select")
			.pipe(filter((e) => e.deselected.length > 0 && e.selected.length === 0))
			.subscribe(clearResource);

		fromEvent(select, "select")
			.pipe(
				filter((e) => e.selected != null && e.selected.length > 0),
				mapOp((e) => e.selected[0]),
				extractUUIDFromEvent
			)
			.subscribe(selectResource);

		fromEvent(select, "select").subscribe((e) => {
			const { selected, deselected } = e;
			selected.forEach((feature) => feature.setStyle(selectedStyle(feature)));
			deselected.forEach((feature) =>
				feature.setStyle(deselectedStyle(feature))
			);
		});

		this.updateStyleSelected
			.pipe(
				filter((item) => !_.isNil(item)),
				mapOp((featureData) => _.get(featureData, "props.resourceId")),
				distinctUntilChanged(),
				mapOp((featureId) => this.features[featureId]),
				filter((item) => !_.isNil(item))
			)
			.subscribe((feature) => feature.setStyle(selectedStyle(feature)));

		this.updateDataSubject
			.pipe(
				filter(() => !this.props.remoteData.isEmpty()),
				mergeMap(() => source.getFeatures()),
				mapOp((feature) => {
					const { remoteData, paletteType, min, max } = this.props;
					const featureId = feature.values_.uuid;
					const data = remoteData.get(featureId);
					const dataValue = _.get(data, "value", 0);
					const color = getColorFeature(dataValue, paletteType, min, max);
					return {
						feature,
						color,
						dataValue
					};
				})
			)
			.subscribe(this.updateFeatureStyle);
	}

	componentDidUpdate() {
		const { selectedResource } = this.props;
		this.sendUpdateEventToStreams();
		this.updateStyleSelected.next(selectedResource);
	}

	componentWillUnmount() {
		this.props.map.removeLayer(this.layer);
	}

	saveVector = (vector) => {
		this.vector = vector;
	};

	updateFeatureStyle = ({ feature, color }) => {
		const styleFeature = new Style({
			fill: new Fill({
				color: color
			}),
			stroke: new Stroke({
				color: "#319FD3",
				width: 1
			})
		});

		feature.set("color", color);
		feature.setStyle(styleFeature);
	};

	sendUpdateEventToStreams = () => {
		if (this.vector != null) {
			const event = new Event("updateData");
			this.updateDataSubject.next(event);
		}
	};

	render() {
		return null;
	}
}

function mapStateToProps(state, props) {
	return {
		remoteData: dataSelector(state, props),
		paletteType: paletteSelector(state),
		mapType: state.filters.get("type"),
		year: state.filters.get("year"),
		min: state.data.get("minValue"),
		max: state.data.get("maxValue"),
		selectedResource: resourceSelector(state)
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			loadContainer: lc,
			addResourceId: ari,
			selectResource: sr,
			clearResource: cr,
			incrementLoadedContainersCount: ilcc
		},
		dispatch
	);
}

export default connect(mapStateToProps, mapDispatchToProps)(MapLayer);



// WEBPACK FOOTER //
// ./src/components/map/layer.js